import { useMemo, useRef, useState } from "react";
import {
	BooleanField,
	BooleanInput,
	DataTable,
	DateField,
	DateInput,
	EditButton,
	FilterButton,
	FunctionField,
	Labeled,
	Link,
	List,
	NumberField,
	NumberInput,
	ReferenceField,
	SelectInput,
	SimpleShowLayout,
	TextField,
	TextInput,
	TopToolbar,
	useListContext,
	useRecordContext,
} from "react-admin";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField as MuiTextField,
	Typography,
} from "@mui/material";

import { useClientContext } from "contexts/client";
import FetchButton from "./components/FetchButton";
import ContactCard from "./components/ContactCard";
import CopyField from "./components/CopyField";
import CopyButton from "./components/CopyButton";
import CSVExportButton from '../../../custom_components/CSVExportButton';
import { SelectArrayInput } from "react-admin";
import { storeStatusList } from "../master_customers/Edit";
import { parsePosSettings } from "./utils";


const SYSTEM_STATUS_URL = 'https://c6a3avi5tysbppzjjrkaoethdi0arems.lambda-url.us-east-1.on.aws/';

const SYSTEM_STATUS_COLUMNS = [
	{ field: 'store_id', label: 'Store ID' },
	{ field: 'store_name', label: 'Store Name' },
	{ field: 'account_id', label: 'Account ID' },
	{ field: 'account_name', label: 'Account' },
	{ field: 'store_status', label: 'Status' },
	{ field: 'schedule', label: 'Schedule' },
	{ field: 'logdateutc', label: 'Last Log' },
	{ field: 'level', label: 'Level' },
	{ field: 'message', label: 'Message' },
	{ field: 'addr1', label: 'Address' },
	{ field: 'city', label: 'City' },
	{ field: 'state', label: 'State' },
	{ field: 'zip', label: 'ZIP' },
];

const SystemStatusDialog = ({ open, onClose }) => {
	const { state: client } = useClientContext();
	const clientPrefix = client.client.resourcePrefix;
	const [params, setParams] = useState({
		store_id: '',
		store_name: '',
		account_id: '',
		account_name: '',
		city: '',
		state: '',
		zip: '',
	});
	const [results, setResults] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [statusText, setStatusText] = useState('');
	const [sortField, setSortField] = useState('store_id');
	const [sortDir, setSortDir] = useState('asc');
	const [columnFilters, setColumnFilters] = useState({});
	const [countdown, setCountdown] = useState(null);
	const countdownRef = useRef(null);
	const abortRef = useRef(null);

	const handleSort = (field) => {
		if (sortField === field) {
			setSortDir(d => d === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDir('asc');
		}
	};

	const sortedResults = useMemo(() => {
		if (!results) return [];
		const filtered = results.filter(row =>
			SYSTEM_STATUS_COLUMNS.every(col => {
				const f = columnFilters[col.field];
				if (!f) return true;
				return String(row[col.field] ?? '').toLowerCase().includes(f.toLowerCase());
			})
		);
		return [...filtered].sort((a, b) => {
			const aVal = a[sortField] ?? '';
			const bVal = b[sortField] ?? '';
			if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
			if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
			return 0;
		});
	}, [results, sortField, sortDir, columnFilters]);

	const handleFetch = async () => {
		setLoading(true);
		setError(null);

		// Start countdown
		setCountdown(90);
		let remaining = 90;
		countdownRef.current = setInterval(() => {
			remaining -= 1;
			setCountdown(remaining);
			if (remaining <= 0) clearInterval(countdownRef.current);
		}, 1000);

		// Set up abort controller for 90s timeout
		const controller = new AbortController();
		abortRef.current = controller;
		const timeoutId = setTimeout(() => controller.abort(), 90000);

		try {
			const body = {
				store_id: params.store_id ? Number(params.store_id) : 0,
				store_name: params.store_name || '',
				account_id: params.account_id ? Number(params.account_id) : 0,
				account_name: params.account_name || '',
				city: params.city || '',
				state: params.state || '',
				zip: params.zip || '',
			};
			const response = await fetch(SYSTEM_STATUS_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
				signal: controller.signal,
			});
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const data = await response.json();
			if (data.status) {
				setResults(data.stores || []);
				setStatusText(data.status_text || '');
			} else {
				setError('Failed to fetch: ' + (data.status_text || 'Unknown error'));
			}
		} catch (e) {
			if (e.name === 'AbortError') {
				setError('Request timed out after 90 seconds.');
			} else {
				setError('Error: ' + e.message);
			}
		} finally {
			clearTimeout(timeoutId);
			clearInterval(countdownRef.current);
			setCountdown(null);
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
			<DialogTitle>System Status</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'flex-end', pt: 1 }}>
					<MuiTextField
						label="Store ID"
						type="number"
						value={params.store_id}
						onChange={(e) => setParams(p => ({ ...p, store_id: e.target.value }))}
						size="small"
						sx={{ width: 120 }}
					/>
					<MuiTextField
						label="Store Name"
						value={params.store_name}
						onChange={(e) => setParams(p => ({ ...p, store_name: e.target.value }))}
						size="small"
					/>
					<MuiTextField
						label="Account ID"
						type="number"
						value={params.account_id}
						onChange={(e) => setParams(p => ({ ...p, account_id: e.target.value }))}
						size="small"
						sx={{ width: 120 }}
					/>
					<MuiTextField
						label="Account Name"
						value={params.account_name}
						onChange={(e) => setParams(p => ({ ...p, account_name: e.target.value }))}
						size="small"
					/>
					<MuiTextField
						label="City"
						value={params.city}
						onChange={(e) => setParams(p => ({ ...p, city: e.target.value }))}
						size="small"
						sx={{ width: 140 }}
					/>
					<MuiTextField
						label="State"
						value={params.state}
						onChange={(e) => setParams(p => ({ ...p, state: e.target.value }))}
						size="small"
						sx={{ width: 80 }}
					/>
					<MuiTextField
						label="ZIP"
						value={params.zip}
						onChange={(e) => setParams(p => ({ ...p, zip: e.target.value }))}
						size="small"
						sx={{ width: 100 }}
					/>
					{countdown !== null && (
						<Typography
							variant="body2"
							sx={{ color: countdown <= 10 ? 'error.main' : 'text.secondary', minWidth: 48, textAlign: 'right' }}
						>
							{countdown}s
						</Typography>
					)}
					<Button variant="contained" onClick={handleFetch} disabled={loading} sx={{ height: 40 }}>
						{loading && <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />}
						Fetch
					</Button>
				</Box>

				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				{results !== null && (
					<>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							{statusText}
						</Typography>
						<TableContainer component={Paper} sx={{ maxHeight: 600 }}>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										{SYSTEM_STATUS_COLUMNS.map(col => (
											<TableCell key={col.field} sx={{ fontWeight: 'bold', pt: '42px', verticalAlign: 'bottom' }}>
												<TableSortLabel
													active={sortField === col.field}
													direction={sortField === col.field ? sortDir : 'asc'}
													onClick={() => handleSort(col.field)}
												>
													{col.label}
												</TableSortLabel>
											</TableCell>
										))}
									</TableRow>
									<TableRow>
										{SYSTEM_STATUS_COLUMNS.map(col => (
											<TableCell key={col.field} sx={{ py: 0.5, bgcolor: 'background.paper' }}>
												<MuiTextField
													size="small"
													placeholder="Filter..."
													value={columnFilters[col.field] ?? ''}
													onChange={(e) => setColumnFilters(prev => ({ ...prev, [col.field]: e.target.value }))}
													inputProps={{ style: { fontSize: 12, padding: '4px 6px' } }}
													sx={{ width: '100%' }}
												/>
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{sortedResults.map((row, i) => (
										<TableRow key={row.log4net_id ?? i} hover>
											<TableCell>
												<Link
													to={`/${clientPrefix}_patron_points_stores/${row.store_id}/store-logs`}
													onClick={onClose}
												>
													{row.store_id}
												</Link>
											</TableCell>
											<TableCell>{row.store_name}</TableCell>
											<TableCell>{row.account_id}</TableCell>
											<TableCell>{row.account_name}</TableCell>
											<TableCell>{row.store_status}</TableCell>
											<TableCell>{row.schedule}</TableCell>
											<TableCell sx={{ whiteSpace: 'nowrap' }}>
												{row.logdateutc ? new Date(row.logdateutc).toLocaleString() : ''}
											</TableCell>
											<TableCell>
												<Typography
													variant="body2"
													sx={{ color: row.level === 'ERROR' ? 'error.main' : 'inherit' }}
												>
													{row.level}
												</Typography>
											</TableCell>
											<TableCell sx={{ maxWidth: 400, wordBreak: 'break-word' }}>
												{row.message}
											</TableCell>
											<TableCell>{row.addr1}</TableCell>
											<TableCell>{row.city}</TableCell>
											<TableCell>{row.state}</TableCell>
											<TableCell>{row.zip}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};

const capitalizeFirstLetter = (str) =>
	str.substring(0, 1).toUpperCase() + str.substring(1, str.length);

const getChips = (data = []) => {
	if (!data) return [];
	// convert all vals to lowercase
	data = data.map((val) => val.toLowerCase());
	// convert to set of uniques, then back to array
	data = [...new Set(data)];
	// capitalize first letter of each unique val
	data = data.map((val) => capitalizeFirstLetter(val));
	// return array of chips
	return data.map((val) => <Chip label={val} />);
};

const PatronPointsStoreFilters = [
	<NumberInput label="ID" source="id" />,
	<TextInput label="Name" source="name" />,
	<TextInput label="Street Address" source="addr1" />,
	<TextInput label="City" source="city" />,
	<TextInput label="State" source="state" />,
	<NumberInput label="ZIP" source="postal_code" />,
	<TextInput label="Formatted Address" source="formatted_address" />,
	<BooleanInput
		label="Has Missing Data"
		source="has_missing_data"
		defaultValue={true}
	/>,
	<BooleanInput
		label="Has InfoBate Match"
		source="has_infobate_match"
		defaultValue={true}
	/>,
	<BooleanInput
		label="OnPoint Participant"
		source="onpoint_participating"
	/>,
	<SelectArrayInput
		label="InfoBate Store Status"
		source="infobate_store_status"
		choices={storeStatusList}
	/>,
	<NumberInput label="MSID" source="infobate_master_customer_id" />,
	<TextInput label="Distributor Name" source="primary_supplier_name" />,
	<NumberInput label="Distributor ID" source="primary_supplier_id" />,
	<NumberInput label="POS Type ID" source="pos_type_id" />,
	// Store Status - get choices
	// Migration Status - get choices
	<SelectInput
		label="Deleted Flag"
		source="deleted_flag"
		choices={["Y", "N"]}
	/>,
	<DateInput
		label="Last File Received Date"
		source="store_settings.last_file_received_date"
	/>,
	<DateInput
		label="Date ID Added"
		source="store_settings.date_id_added"
	/>
];

const ListActions = () => {
	const { sort, setSort, filterValues } = useListContext();
	const [systemStatusOpen, setSystemStatusOpen] = useState(false);

	return (
		<TopToolbar sx={{ minHeight: 0, padding: 0 }}>
			<Box sx={{
				position: 'fixed',
				top: '48px',
				right: 0,
				zIndex: 1200,
				display: 'flex',
				flexDirection: 'row',
				gap: '.5em',
				alignItems: 'center',
				backgroundColor: 'background.paper',
				padding: '8px 16px',
				boxShadow: 2,
				borderRadius: '0 0 0 8px',
			}}>
				<Button variant="outlined" onClick={() => setSystemStatusOpen(true)}>
					System Status
				</Button>
				<Button onClick={() => setSort({ field: 'id', order: 'DESC' })}>
					Reset Sort
				</Button>
				<FetchButton
					type="new"
					label="Fetch New"
					variant="outlined"
					requireConfirm
				/>
				<FilterButton />
				<CSVExportButton
					resource={`onpoint_patron_points_stores`}
					filename="onpoint_patron_points_stores"
					sort={sort}
					filterValues={filterValues}
				/>
			</Box>
			<SystemStatusDialog open={systemStatusOpen} onClose={() => setSystemStatusOpen(false)} />
		</TopToolbar>
	);
};

const ExpandedRow = () => {
	const record = useRecordContext();
	const { state: client } = useClientContext();
	const clientPrefix = client.client.resourcePrefix;

	if (!record.name) return null;

	const getContactObject = (prefix) =>
		Object.keys(record)
			.filter((key) => key.startsWith(prefix))
			.reduce((obj, key) => {
				obj[key.replace(prefix, "")] = record[key];
				return obj;
			}, {});

	const getFormattedPhone = () =>
		record?.phone_number
			? `(${record.phone_number.substring(
					0,
					3
			  )}) ${record.phone_number.substring(
					3,
					6
			  )}-${record.phone_number.substring(6, 11)}`
			: "N/A";
	const primaryContact = getContactObject("primary_contact_");
	const ownerContact = getContactObject("owner_");

	return (
		<SimpleShowLayout>
			<Divider variant="fullWidth" />
			<Container
				sx={{
					display: "flex",
					flexFlow: "row wrap",
					gap: "2em",
					padding: 0,
					margin: 0,
					width: "100%",
					height: "100%",
				}}
			>
				<Stack sx={{ gap: "1em" }}>
					<Typography variant="h6">Store Info</Typography>
					<Box
						sx={{
							display: "flex",
							flexFlow: "row wrap",
							gap: "1em",
						}}
					>
						<Stack sx={{ gap: "1em" }}>
							<CopyField source="name" />
							<CopyField source="id" label="ID" />
							<Labeled>
								<TextField
									source="store_status"
									label="Store Status"
									emptyText="N/A"
								/>
							</Labeled>
							<Labeled>
								<TextField
									source="migration_status"
									label="Migration Status"
									emptyText="N/A"
								/>
							</Labeled>
						</Stack>

						<Divider orientation="vertical" variant="middle" flexItem />

						<Stack sx={{ gap: "1em" }}>
							<CopyField source="formatted_address" label="Formatted Address" />
							<CopyField source="addr1" label="Street Address" />
							<CopyField source="city" />
							<CopyField source="state" />
							<CopyField source="postal_code" label="ZIP" />
						</Stack>
					</Box>

					<Divider variant="middle" />

					<Typography variant="h6">Contacts</Typography>
					<Stack sx={{ gap: "1em" }}>
						<Box
							sx={{
								display: "flex",
								flexFlow: "row wrap",
								gap: "1em",
								margin: 0,
							}}
						>
							<Labeled>
								<FunctionField
									source="phone_number"
									label="Phone Number"
									render={(record) =>
										record?.phone_number === "0" ? "N/A" : getFormattedPhone()
									}
								/>
							</Labeled>

							{record?.phone_number === "0" ? null : (
								<CopyButton copyText={record?.phone_number} />
							)}
						</Box>

						<Box
							sx={{
								display: "flex",
								flexFlow: "row wrap",
								gap: "1em",
								margin: 0,
							}}
						>
							<Labeled>
								<FunctionField
									label="Owner"
									render={() =>
										ownerContact ? (
											<ContactCard contact={ownerContact} />
										) : (
											<span>N/A</span>
										)
									}
								/>
							</Labeled>
							<Labeled>
								<FunctionField
									label="Primary Contact"
									render={() =>
										primaryContact ? (
											<ContactCard contact={primaryContact} />
										) : (
											<span>N/A</span>
										)
									}
								/>
							</Labeled>
						</Box>
					</Stack>
				</Stack>

				<Divider orientation="vertical" variant="middle" flexItem />

				<Stack sx={{ gap: "1em" }}>
					<Typography variant="h6">Matching Data</Typography>
					<Box
						sx={{
							display: "flex",
							flexFlow: "row wrap",
							gap: "1em",
						}}
					>
						<Labeled>
							<BooleanField
								source="has_infobate_match"
								label="Has InfoBate Match"
								looseValue
							/>
						</Labeled>
						<Divider orientation="vertical" variant="middle" flexItem />
						<CopyField
							source="infobate_master_customer_id"
							label="MSID"
							emptyText="N/A"
						/>
					</Box>
					<EditButton
						disabled={!record.has_infobate_match}
						variant="outlined"
						label="View master record"
						record={{ id: record?.infobate_master_customer_id }}
						resource={`${clientPrefix}_master_customers`}
					/>

					<Divider variant="middle" />

					<Typography variant="h6">Distributor Data</Typography>
					<Box
						sx={{
							display: "flex",
							flexFlow: "row wrap",
							gap: "1em",
						}}
					>
						<CopyField source="primary_supplier_id" label="Distributor ID" />
						<Divider orientation="vertical" variant="middle" flexItem />
						<CopyField
							source="primary_supplier_name"
							label="Distributor Name"
						/>
					</Box>

					<Divider variant="middle" />

					<Typography variant="h6">POS Data</Typography>
					<Stack sx={{ gap: "1em" }}>
						<Box
							sx={{
								display: "flex",
								flexFlow: "row wrap",
								gap: "1em",
							}}
						>
							<Labeled>
								<NumberField
									source="store_settings.pos_type_id"
									label="POS Type ID"
									emptyText="N/A"
								/>
							</Labeled>
							<Divider orientation="vertical" variant="middle" flexItem />
							<Labeled>
								<FunctionField
									source="reported_pos_type"
									label="POS Type"
									render={(record) => getChips(record?.reported_pos_type)}
									emptyText="N/A"
								/>
							</Labeled>
						</Box>

						<Labeled>
							<FunctionField
								label="POS Settings"
								render={(record) => {
									if (!record?.pos_settings) return <span>N/A</span>;
									const parsed = parsePosSettings(record.pos_settings);
									let settingsString = parsed
										? JSON.stringify(parsed, null, 2)
										: record.pos_settings;

									return (
										<Paper
											sx={{
												position: "relative",
												display: "flex",
												gap: "1em",
												maxWidth: "300px",
											}}
										>
											<CopyButton
												copyText={settingsString}
												sx={{
													position: "absolute",
													display: "inline-block",
													top: ".25em",
													right: ".25em",
												}}
											/>

											<pre
												style={{
													padding: ".5em",
													overflowX: "scroll",
												}}
											>
												<code>{settingsString}</code>
											</pre>
										</Paper>
									);
								}}
							/>
						</Labeled>
					</Stack>
				</Stack>

				<Divider orientation="vertical" variant="middle" flexItem />

				<Stack sx={{ gap: "1em" }}>
					<Typography variant="h6">Activity</Typography>
					<Box
						sx={{
							display: "flex",
							flexFlow: "row wrap",
							gap: "1em",
						}}
					>
						<Stack sx={{ gap: "1em" }}>
							<Labeled>
								<BooleanField
									source="has_missing_data"
									label="Has Missing Data"
								/>
							</Labeled>
							<Labeled>
								<DateField
									source="store_settings.last_transaction_date"
									label="Last Transaction"
									emptyText="N/A"
								/>
							</Labeled>
							<Labeled>
								<DateField
									source="store_settings.date_id_added"
									label="ID Added"
									emptyText="N/A"
								/>
							</Labeled>
							<Labeled>
								<DateField
									source="store_settings.date_id_inactive"
									label="ID Inactive"
									emptyText="N/A"
								/>
							</Labeled>
						</Stack>

						<Divider orientation="vertical" variant="middle" flexItem />

						<Stack sx={{ gap: "1em" }}>
							<Labeled>
								<NumberField
									source="file_count"
									label="File Count"
									emptyText="N/A"
								/>
							</Labeled>
							<Labeled>
								<DateField
									source="store_settings.last_file_received_date"
									label="Last File Received"
									emptyText="N/A"
								/>
							</Labeled>
							<Labeled>
								<DateField
									source="store_settings.first_file_received_date"
									label="First File Received"
									emptyText="N/A"
								/>
							</Labeled>
						</Stack>
					</Box>
				</Stack>
			</Container>
		</SimpleShowLayout>
	);
};

const PatronPointsStoreList = (props) => {
	const { state: client } = useClientContext();
	const clientPrefix = client.client.resourcePrefix;

	return (
		<List
			{...props}
			actions={<ListActions />}
			filter={{ enabled: true }}
			filters={PatronPointsStoreFilters}
			sort={{ field: "id", order: "DESC" }}
			title="Patron Points Stores"
		>
			<DataTable expand={<ExpandedRow />} expandSingle>
				{/* Store */}
				<DataTable.Col source="id" label="ID" />
				<DataTable.Col source="name" />

				{/* Matching */}
				<DataTable.Col
					source="infobate_master_customer_id"
					label="MSID"
					render={(record) =>
						record?.has_infobate_match ? (
							<Link
								to={`/${clientPrefix}_master_customers/${record?.infobate_master_customer_id}`}
								onClick={(event) => event.stopPropagation()}
								sx={{ color: '#6392b0' }}
							>
								{record?.infobate_master_customer_id}
							</Link>
						) : (
							"N/A"
						)
					}
				/>
				<DataTable.Col
					source="primary_supplier_id"
					label="Distributor ID"
				/>
				<DataTable.Col
					source="primary_supplier_name"
					label="Distributor Name"
				/>

				{/* Meta */}
				<DataTable.Col source="store_status" label="Store Status" />
				<DataTable.Col source="migration_status" label="Migration Status" />
				<DataTable.Col
					source="store_settings.deleted_flag"
					label="Deleted Flag"
				/>
				<DataTable.Col
					source="reported_pos_type"
					label="Reported POS Type"
					render={(record) => getChips(record?.reported_pos_type || [])}
				/>
				<DataTable.Col source="last_reported" label="Last Reported" />
				<DataTable.Col label="Last Updated">
					<ReferenceField
						source="id"
						reference="onpoint_patron_points_daily_stores_updated_times"
					>
						<DateField source="updated_at" showTime />
					</ReferenceField>
				</DataTable.Col>

				{/* Store address */}
				<DataTable.Col source="addr1" label="Street Address" />
				<DataTable.Col source="city" label="City" />
				<DataTable.Col source="state" label="State" />
				<DataTable.Col source="postal_code" label="ZIP" />

				{/* Update button */}
				<DataTable.Col label="Update">
					<FetchButton type="single" />
				</DataTable.Col>
			</DataTable>
		</List>
	);
};

export default PatronPointsStoreList;
